import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import XCircle from "~/components/XCircle.jsx";
import Sidebar from "~/components/Sidebar.jsx";
import ModalContent from "~/components/ModalContent.jsx";

const COLLECTION_HANDLES = [
  "bright-light",
  "light",
  "medium",
  "full-medium",
  "dark",
  "beyond-dark",
];

const ShopByRoastModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCollection, setActiveCollection] = useState("medium");
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const storefrontAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESSTOKEN;
  const shopifyDomain = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const activeCollectionHandler = (e) => {
    const collectionHandle = e.target.getAttribute("roastlevel");
    const collection = collections.find((c) => c.collectionHandle === collectionHandle);
    setActiveCollection(collection);
  };

  useEffect(() => {
    const toggleOverflow = (isDisabled) => {
      document.documentElement.style.overflow = isDisabled ? "hidden" : "";
      document.body.style.overflow = isDisabled ? "hidden" : "";
    };

    const handleMobileCloseButton = (isHidden) => {
      if (window.innerWidth < 768) {
        const drawerCloseBtn = document.querySelector(
          ".mobile-draw .mfp-close.mfp-close--custom.js-close-mfp"
        );
        if (drawerCloseBtn) {
          drawerCloseBtn.classList.toggle("!hidden", isHidden);
        }
      }
    };

    if (isOpen) {
      toggleOverflow(true);
      handleMobileCloseButton(true);
    } else {
      toggleOverflow(false);
      handleMobileCloseButton(false);
    }

    return () => {
      toggleOverflow(false);
    };
  }, [isOpen]);

  useEffect(() => {
    async function fetchAllProducts(handle, cursor = null, allProducts = []) {
      // console.log("📌 Fetching collection:", handle, "with cursor:", cursor);

      const query = `
        {
          collectionByHandle(handle: "${handle}") {
            id
            title
            description
            image {
              src
              altText
            }
            metafield(namespace: "custom", key: "shop_by_roast_collection_subtitle") {
              value
              type
            }
            products(first: 50, after: ${cursor ? `"${cursor}"` : null}) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  handle
                  onlineStoreUrl
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  variants(first: 50) {
                    edges {
                      node {
                        price {
                          amount
                          currencyCode
                        }
                        title
                      }
                    }
                  }
                  images(first: 1) {
                    edges {
                      node {
                        src
                        altText
                      }
                    }
                  }
                  media(first: 1) {
                    edges {
                      node {
                        ... on Video {
                          sources {
                            url
                            format
                          }
                          previewImage {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        }
      `;

      try {
        const response = await fetch(`https://${shopifyDomain}/api/2025-01/graphql.json`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": storefrontAccessToken,
          },
          body: JSON.stringify({ query }),
        });

        const data = await response.json();
        const collection = data?.data?.collectionByHandle;

        if (!collection) {
          console.warn(`⚠️ No data found for collection: ${handle}`);
          return { collectionHandle: handle, products: [] };
        }

        const products = collection?.products?.edges
          .map((edge) => edge.node)
          .filter(
            (product) =>
              typeof product === "object" &&
              product.id &&
              product.title &&
              product.priceRange &&
              typeof product.priceRange.minVariantPrice.amount !== "undefined" &&
              typeof product.priceRange.maxVariantPrice.amount !== "undefined"
          );

        const uniqueProducts = Array.from(
          new Map([...allProducts, ...products].map((p) => [p.id, p])).values()
        );

        // console.log(`✅ Collection: ${handle} - Found ${uniqueProducts.length} products`);

        if (collection?.products?.pageInfo?.hasNextPage) {
          return fetchAllProducts(handle, collection.products.pageInfo.endCursor, uniqueProducts);
        }

        return {
          collectionHandle: handle,
          collectionId: collection?.id,
          collectionDescription: collection?.description,
          collectionImage: collection?.image,
          products: uniqueProducts,
          collectionSubtitle: collection?.metafield?.value || "",
        };
      } catch (error) {
        console.error(`❌ Error fetching collection: ${handle}`, error);
        return {
          collectionHandle: handle,
          collectionId: collection?.id,
          collectionDescription: collection?.description,
          collectionImage: collection?.image,
          products: [],
          collectionSubtitle: collection?.metafield?.value || "",
        };
      }
    }

    async function fetchMultipleCollections() {
      try {
        // console.log("🛠 Fetching collections:", COLLECTION_HANDLES);

        const collectionsData = await Promise.all(
          COLLECTION_HANDLES.map((handle) => fetchAllProducts(handle))
        );

        // console.log("📌 Final Structured Data:", collectionsData);
        setCollections(collectionsData);

        const mediumCollection = collectionsData.find(
          (collection) => collection.collectionHandle === "medium"
        );
        setActiveCollection(mediumCollection || collectionsData[0]);
      } catch (err) {
        setError("Failed to fetch collections");
        console.error("❌ Error fetching multiple collections:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchMultipleCollections();
  }, []);

  return (
    <div className="page-container">
      <button
        id="shop-by-roast-desktop-btn"
        className="shop-by-roast-nav-btn shop-by-roast-linear-gradient"
        onClick={openModal}>
        Shop By Roast
      </button>

      {isOpen && (
        <>
          <div className="fixed w-[100vw] h-[100vh] top-0 left-0 bg-gray-800 opacity-50 z-40"></div>
          <div
            className="fixed w-[100vw] h-[100vh] top-0 left-0 flex items-center justify-center z-2000"
            onClick={handleBackgroundClick}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="bg-white shadow-lg w-97.5 h-[90vh] md:h-auto mx-auto md:max-w-4xl md:w-full flex relative">
              <XCircle onClick={closeModal} />
              <Sidebar
                collections={collections}
                activeCollectionHandler={activeCollectionHandler}
                activeCollection={activeCollection}
              />
              <ModalContent activeCollection={activeCollection} />
            </motion.div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShopByRoastModal;
